import { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  Align,
  Grid,
  GridCol,
  GridRow,
  Text,
  VerticalSpace,
} from "@arc-ui/components";
import {
  useInfiniteHits,
  useInstantSearch,
  useStats,
  useMenu,
} from "react-instantsearch";
import { MIN_SKELETON_MS, HITS_PER_PAGE } from "../../../constants";
import { useAccessibility, useDeviceType } from "../../../hooks";
import { FacetSkeleton, NoResults, ResultSkeleton } from "../../atoms";
import { ResultsHeaderLive, ResultsList } from "../../molecules";
import SearchFilters from "../SearchFilters/SearchFilters";

import { AlgoliaHit } from "../../../types/algolia-hit";
import { trackSearchAnalytics } from "../../../utils/datalayer-events";

export const ResultsArea = () => {
  const { isMobile, isTablet } = useDeviceType();
  const { nbHits } = useStats();
  const { status, indexUiState } = useInstantSearch();
  const { items: facetBuckets } = useMenu({ attribute: "category" });
  const { announce } = useAccessibility();

  const trimmedQuery = (indexUiState?.query || "").trim();

  const prevQuery = useRef<string>("");
  const startTimeRef = useRef<number>(0);
  const [showFullSkeleton, setShowFullSkeleton] = useState(false);

  useEffect(() => {
    if (trimmedQuery && trimmedQuery !== prevQuery.current) {
      prevQuery.current = trimmedQuery;
      if(!showFullSkeleton){
// eslint-disable-next-line react-hooks/exhaustive-deps
        setShowFullSkeleton(true)
      } 
      startTimeRef.current = Date.now();
      announce(`Searching for ${trimmedQuery}`);
    }
  }, [trimmedQuery, announce]);

  useEffect(() => {
    if (!showFullSkeleton) return;

    if (status === "error" && showFullSkeleton) {
      setShowFullSkeleton(false)
      return;
    }

    if (status === "idle") {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(MIN_SKELETON_MS - elapsed, 0);

      const timer = setTimeout(() => {
        setShowFullSkeleton(false);
        announce(`Found ${nbHits} results for ${trimmedQuery}`);

        trackSearchAnalytics(
          "search result page load",
          trimmedQuery,
          nbHits,
          "All results",
        );
      }, remaining);

      return () => clearTimeout(timer);
    }
  }, [status, showFullSkeleton, nbHits, trimmedQuery, announce]);

  const skeletonKeys = useMemo(
    () => Array.from({ length: HITS_PER_PAGE }, () => uuidv4()),
    [],
  );

  const isLoadingNextPage = status !== "idle";
  const { items, showMore, isLastPage, sendEvent } =
    useInfiniteHits<AlgoliaHit>();
  const hasError = status === "error";
  const isStalled = status === "stalled";

  const renderResults = () => {
    if (hasError) {
      return (
        <div role="alert">
          <Align horizontal="center">
            <Text size="l">
              <b>We couldn&apos;t load your results</b>
            </Text>
            <VerticalSpace size="20" />
            <Text size="m">
              Something went wrong while loading search results. Please try
              again.
            </Text>
          </Align>
          <VerticalSpace size="32" />
        </div>
      );
    }

    if (nbHits === 0 && !isStalled) {
      return (
        <>
          {!isMobile && !isTablet && (
            <ResultsHeaderLive
              facetBuckets={facetBuckets}
              nbHits={nbHits}
              align="left"
            />
          )}
          <NoResults />
        </>
      );
    }

    return (
      <>
        {!isMobile && !isTablet && (
          <ResultsHeaderLive
            facetBuckets={facetBuckets}
            nbHits={nbHits}
            align="left"
          />
        )}
        <ResultsList
          items={items}
          nbHits={nbHits}
          isLastPage={isLastPage}
          isLoadingNextPage={isLoadingNextPage}
          query={trimmedQuery}
          onShowMore={showMore}
          sendEvent={sendEvent}
        />
      </>
    );
  };

  if (!trimmedQuery) return null;

  if (showFullSkeleton && !hasError) {
    return (
      <div className="ee-search-resultsArea">
        <Grid isFluid>
          <GridRow align="start">
            <GridCol xs={12} s={12} m={12} l={3} xl={4}>
              <FacetSkeleton />
            </GridCol>
            <GridCol xs={12} s={12} m={12} l={9} xl={8}>
              {skeletonKeys.map((key) => (
                <ResultSkeleton key={key} />
              ))}
            </GridCol>
          </GridRow>
        </Grid>
      </div>
    );
  }

  return (
    <div className="ee-search-resultsArea">
      <Grid isGutterless={isMobile || isTablet}>
        <GridRow>
          <GridCol xs={12} s={12} m={12} l={3} xl={4}>
            <SearchFilters
              attribute="category"
              facetBuckets={facetBuckets}
              nbHits={nbHits}
            />
          </GridCol>

          <GridCol xs={12} s={12} m={12} l={9} xl={8}>
            <div className="ee-search-resultsContainer">{renderResults()}</div>
          </GridCol>
        </GridRow>
      </Grid>
    </div>
  );
};

export default ResultsArea;
